"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import type { BlokProps } from "@trydig/storyblok";
import { useState, useTransition } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import { BlokWrap } from "@/components/bloks/BlokWrap";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Container } from "@/components/ui/Container";
import { CheckboxField } from "@/components/ui/form/CheckboxField";
import { InputField } from "@/components/ui/form/InputField";
import { Typography } from "@/components/ui/Typography";
import { useTranslations } from "@/i18n/client";
import { trackGenerateLead } from "@/lib/gtm";
import { joinMembersClub, type MembersClubResult } from "./actions";
import { type MembersClubFormInput, membersClubFormSchema } from "./schema";

// Minimal shape mirrored from the Storyblok schema — regenerate via
// `pnpm generate:storyblok` to replace with the generated interface.
export interface MembersClubFormStoryblok {
	heading?: string;
	component: "membersClubForm";
	_uid: string;
	[k: string]: unknown;
}

export const MembersClubFormBlok = ({ blok }: BlokProps<MembersClubFormStoryblok>) => {
	const t = useTranslations("membersClubForm");
	// tRaw resolves fully-qualified keys returned by the schema / server action
	// (e.g. "membersClubForm.fieldError.emailInvalid"). The static references
	// below are what push-translations.ts picks up:
	//
	//   tRaw("membersClubForm.fieldError.emailRequired")
	//   tRaw("membersClubForm.fieldError.emailInvalid")
	//   tRaw("membersClubForm.fieldError.firstNameRequired")
	//   tRaw("membersClubForm.fieldError.lastNameRequired")
	//   tRaw("membersClubForm.fieldError.necessaryEmailRequired")
	const tRaw = useTranslations();
	const [alert, setAlert] = useState<MembersClubResult | null>(null);
	const [isPending, startTransition] = useTransition();

	const methods = useForm<MembersClubFormInput>({
		resolver: zodResolver(membersClubFormSchema),
		defaultValues: {
			email: "",
			firstName: "",
			lastName: "",
			phone: "",
			necessaryEmail: undefined,
			promotionalEmail: false,
		},
	});

	const onSubmit: SubmitHandler<MembersClubFormInput> = (values) => {
		setAlert(null);
		startTransition(async () => {
			const result = await joinMembersClub(values);

			if (!result.ok && result.reason === "validation") {
				for (const [field, message] of Object.entries(result.fieldErrors)) {
					if (message) {
						methods.setError(field as keyof MembersClubFormInput, { message });
					}
				}
				return;
			}

			if (result.ok) {
				methods.reset();
				trackGenerateLead({ form_name: "members_club" });
			}
			setAlert(result);
		});
	};

	const resolveError = (message: string | undefined) => (message ? tRaw(message) : undefined);

	return (
		<BlokWrap
			editable={blok}
			className="bg-background-bg-sand-1 py-grid-section-vertical-lg w-full"
		>
			<Container>
				<div className="flex flex-col gap-grid-section-vertical-lg max-w-[955px] mx-0">
					<Typography as="h2" variant="heading-display-sm" className="text-text-dark-text-1">
						{blok.heading || t("heading")}
					</Typography>

					<FormProvider {...methods}>
						<form
							noValidate
							onSubmit={methods.handleSubmit(onSubmit)}
							className="flex flex-col gap-grid-section-vertical-md w-full"
						>
							<div className="flex flex-col gap-grid-section-vertical-md w-full">
								<InputField
									name="email"
									type="email"
									autoComplete="email"
									label={t("emailLabel")}
									placeholder={t("emailPlaceholder")}
									disabled={isPending}
									errorMessage={resolveError(methods.formState.errors.email?.message)}
								/>
								<InputField
									name="firstName"
									autoComplete="given-name"
									label={t("firstNameLabel")}
									placeholder={t("firstNamePlaceholder")}
									disabled={isPending}
									errorMessage={resolveError(methods.formState.errors.firstName?.message)}
								/>
								<InputField
									name="lastName"
									autoComplete="family-name"
									label={t("lastNameLabel")}
									placeholder={t("lastNamePlaceholder")}
									disabled={isPending}
									errorMessage={resolveError(methods.formState.errors.lastName?.message)}
								/>
								<InputField
									name="phone"
									type="tel"
									autoComplete="tel"
									label={t("phoneLabel")}
									placeholder={t("phonePlaceholder")}
									isOptional
									disabled={isPending}
									errorMessage={resolveError(methods.formState.errors.phone?.message)}
								/>
							</div>

							<div className="flex flex-col gap-grid-section-vertical-sm w-full">
								<CheckboxField name="necessaryEmail" size="large">
									{t("necessaryEmailLabel")}
								</CheckboxField>
								<CheckboxField name="promotionalEmail" size="large">
									{t("promotionalEmailLabel")}
								</CheckboxField>
							</div>

							<Button
								type="submit"
								variant="solid"
								color="primary"
								size="large"
								loading={isPending}
								className="w-full"
							>
								{t("submitLabel")}
							</Button>

							{alert?.ok && (
								<Alert
									variant="success"
									closable
									onClose={() => setAlert(null)}
									closeLabel={t("alert.closeLabel")}
									title={t("success.title")}
								/>
							)}
							{alert && !alert.ok && alert.reason === "server" && (
								<Alert
									variant="error"
									closable
									onClose={() => setAlert(null)}
									closeLabel={t("alert.closeLabel")}
									title={t("error.title")}
									description={t("error.description")}
								/>
							)}
						</form>
					</FormProvider>
				</div>
			</Container>
		</BlokWrap>
	);
};
